export {};

type PendingLock = {
  code: string;
  type: "hidden" | "pagehide";
  detectedAt: string;
  acknowledged: boolean;
};

const PREFIX = "competenceo-secure-qcm-pending-lock:";
const originalFetch = window.fetch.bind(window);
let flushing = false;

function key(code: string) {
  return `${PREFIX}${code}`;
}

function detectCode() {
  return window.location.pathname.match(/^\/qcm\/([0-9]{6})/)?.[1] ?? "";
}

function read(code: string): PendingLock | null {
  try {
    const raw = localStorage.getItem(key(code));
    if (!raw) return null;
    const value = JSON.parse(raw) as PendingLock;
    return value?.code === code ? value : null;
  } catch {
    return null;
  }
}

function write(value: PendingLock) {
  localStorage.setItem(key(value.code), JSON.stringify(value));
}

function remove(code: string) {
  localStorage.removeItem(key(code));
  delete document.documentElement.dataset.secureQcmOfflineLock;
}

function isAnsweringSecureQuestion() {
  return Boolean(document.querySelector(".secure-qcm-question"));
}

function forceLocalLocked() {
  document.documentElement.dataset.secureQcmOfflineLock = "true";
}

function storeViolation(type: "hidden" | "pagehide") {
  const code = detectCode();
  if (!code || !isAnsweringSecureQuestion()) return;
  if (!read(code)) {
    write({ code, type, detectedAt: new Date().toISOString(), acknowledged: false });
  }
  forceLocalLocked();
}

async function flushPending(code = detectCode()) {
  if (!code || !navigator.onLine || flushing) return;
  const pending = read(code);
  if (!pending || pending.acknowledged) return;
  flushing = true;
  try {
    const response = await originalFetch(`/api/secure-qcm/join/${code}/event`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: pending.type,
        detail: {
          detectedAt: pending.detectedAt,
          replayedAfterOffline: true,
          source: "local-pending-lock",
        },
      }),
      keepalive: true,
    });
    if (response.ok) {
      write({ ...pending, acknowledged: true });
      forceLocalLocked();
    }
  } catch {
    // Le verrou reste stocké. Le prochain retour réseau retentera.
  } finally {
    flushing = false;
  }
}

window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  const response = await originalFetch(input, init);
  const url = typeof input === "string" ? input : input instanceof URL ? input.pathname : input.url;
  const match = url.match(/^\/api\/secure-qcm\/join\/([0-9]{6})(?:\?.*)?$/);
  if (!match || (init?.method && init.method !== "GET")) return response;

  const code = match[1];
  const pending = read(code);
  if (!pending || !response.ok) return response;

  try {
    const body = await response.clone().json();
    const status = body?.session?.status;

    if (status === "finished" || status === "done") {
      remove(code);
      return response;
    }

    if (!pending.acknowledged) {
      if (status === "locked") {
        write({ ...pending, acknowledged: true });
      } else if (navigator.onLine) {
        void flushPending(code);
      }
      if (body?.session) body.session.status = "locked";
      body.lockedAt = body.lockedAt ?? pending.detectedAt;
      body.lockReason = body.lockReason ?? pending.type;
      return new Response(JSON.stringify(body), {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
    }

    if (status === "locked") return response;

    // Le serveur avait bien enregistré le verrou, puis renvoie maintenant ACTIVE :
    // cela ne peut arriver qu'après une action explicite du professeur.
    if (status === "question") {
      remove(code);
      return response;
    }

    return response;
  } catch {
    return response;
  }
};

window.addEventListener("online", () => void flushPending());
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") storeViolation("hidden");
  else void flushPending();
});
window.addEventListener("pagehide", () => storeViolation("pagehide"));

window.setTimeout(() => {
  const code = detectCode();
  if (code && read(code)) forceLocalLocked();
  if (navigator.onLine) void flushPending(code);
}, 0);
